import React, { useState, useContext, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { API, APP_NAME, AuthContext } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const Login = () => {
  const navigate = useNavigate();
  const { login } = useContext(AuthContext);
  const logoUrl = "https://yourpersonalshopper-storage.sfo3.digitaloceanspaces.com/assets/bo_logo.webp";
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [loading, setLoading] = useState(false);

  // Update page title
  useEffect(() => {
    document.title = `Login - ${APP_NAME}`;
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, formData);
      login(response.data.token, response.data.shop);
      toast.success("Welcome back!");
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed. Please check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a1a1f] via-[#005f6b] to-[#0a1a1f] flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4 cursor-pointer" onClick={() => navigate("/")}>
            <img src={logoUrl} alt="Logo" className="w-14 h-14 rounded-full" />
            <span className="text-2xl font-semibold text-[#d4af37]">BackOffice</span>
          </div>
          <h1 className="text-3xl font-bold text-[#f8f8f8] mb-2">Welcome Back</h1>
          <p className="text-[#f8f8f8]/70">Login to your dashboard</p>
        </div>

        <div className="bg-[#0a1a1f]/80 backdrop-blur-sm rounded-2xl border border-[#d4af37]/20 p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="email" className="text-[#f8f8f8]">Email</Label>
              <Input
                id="email"
                data-testid="login-email-input"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="you@business.com"
                required
                className="mt-2 bg-[#005f6b]/20 border-[#005f6b] text-[#f8f8f8] placeholder:text-[#f8f8f8]/40 focus:border-[#00ced1] focus:ring-[#00ced1]"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-[#f8f8f8]">Password</Label>
              <Input
                id="password"
                data-testid="login-password-input"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="••••••••"
                required
                className="mt-2 bg-[#005f6b]/20 border-[#005f6b] text-[#f8f8f8] placeholder:text-[#f8f8f8]/40 focus:border-[#00ced1] focus:ring-[#00ced1]"
              />
              <div className="mt-2 text-right">
                <Link to="/forgot-password" title="Forgot Password?" className="text-xs text-[#00ced1] hover:text-[#d4af37] font-medium">
                  Forgot Password?
                </Link>
              </div>
            </div>

            <Button
              data-testid="login-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full bg-[#00ced1] hover:bg-[#00ced1]/90 text-[#0a1a1f] font-semibold py-6 rounded-xl text-base"
            >
              {loading ? "Logging in..." : "Login"}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-[#f8f8f8]/70">
              Don't have an account?{" "}
              <Link to="/signup" className="text-[#00ced1] hover:text-[#d4af37] font-medium">
                Sign up
              </Link>
            </p>
          </div>

          <div className="mt-6 text-center text-xs space-x-4">
            <Link to="/privacy-policy" className="text-[#00ced1] hover:text-[#d4af37] font-medium">
              Privacy Policy
            </Link>
            <Link to="/help-guide" className="text-[#00ced1] hover:text-[#d4af37] font-medium">
              Help Guide
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
